import { useForm } from '@/common/form/FormContext';
import { useFormField } from '@/common/form/hooks/useFormField';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { StringUtils } from '@/common/utils/StringUtils';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

export const UserSelect = ({ stepNum, apprGroup, jobIds, apprGroupToUsers }) => {
  const { principal } = usePrincipal();

  const validate = () => {
    if (internalValue.length !== jobIds.length) {
      return `${jobIds.length - internalValue.length} more users must be selected`;
    }
    return null;
  };

  const { formValues, assignValue } = useForm();
  const { onBlur, error, helperText } = useFormField(
    `step${stepNum}${StringUtils.toUpperCamel(apprGroup)}`,
    jobIds,
    validate,
    `${jobIds.length} User${jobIds.length > 1 ? 's' : ''} to be selected`,
  );

  const [internalValue, setInternalValue] = useState([]);

  const selectableUsers = useMemo(() => {
    const internallySelectedUsernames = internalValue.map((user) => user.username);
    const externallySelectedUsernames = Object.values(formValues).filter(
      (username) => !internallySelectedUsernames.includes(username),
    );

    let users = (apprGroupToUsers[apprGroup] ?? [])
      .filter((user) => !externallySelectedUsernames.includes(user.username))
      .filter((user) => user.username !== principal.username);
    return users;
  }, [formValues]);

  useEffect(() => {
    jobIds.forEach((jobId, index) => {
      assignValue(jobId, internalValue[index]?.username);
    });
  }, [internalValue]);

  return (
    <FormControl error={error}>
      <InputLabel>{apprGroup}</InputLabel>
      <Select
        multiple
        value={internalValue}
        onChange={(event) => setInternalValue(event.target.value)}
        onBlur={onBlur}
        // Render selected users as chips instead of the full MenuItem content
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((user) => (
              <Chip key={user.username} label={`${user.firstName} ${user.lastName}`} />
            ))}
          </Box>
        )}
      >
        {selectableUsers.map((selectableUser) => {
          const isSelected = internalValue.some((user) => user.username === selectableUser.username);
          const disableOption = internalValue.length >= jobIds.length && !isSelected;
          return (
            <MenuItem key={selectableUser.username} value={selectableUser} disabled={disableOption}>
              <Checkbox checked={isSelected} />
              <ListItemText>{`${selectableUser.firstName} ${selectableUser.lastName}`}</ListItemText>
            </MenuItem>
          );
        })}
      </Select>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};
